import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, Clock, CheckCircle, AlertCircle, Send, Calendar, Download, FileText, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AssignmentsWidget = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [submitNotes, setSubmitNotes] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/assignments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAssignments(data.assignments || []);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitAssignment = async (assignmentId) => {
    try {
      setUploading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      let fileUrl = null;
      
      // Upload file first if selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        
        const uploadResponse = await fetch(`${backendUrl}/api/assignments/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          fileUrl = uploadData.file_url || uploadData.filename;
        } else {
          const uploadError = await uploadResponse.json();
          toast.error(uploadError.detail || 'Failed to upload file');
          setUploading(false);
          return;
        }
      }
      
      // Submit assignment with notes and optional file URL
      const response = await fetch(`${backendUrl}/api/assignments/${assignmentId}/submit`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          notes: submitNotes,
          file_url: fileUrl
        })
      });

      if (response.ok) {
        toast.success('Assignment submitted successfully!');
        setSubmitNotes('');
        setSelectedFile(null);
        setSubmitting(null);
        fetchAssignments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit assignment');
      }
    } catch (error) {
      console.error('Error submitting assignment:', error);
      toast.error('Failed to submit assignment');
    } finally {
      setUploading(false);
    }
  };
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      // Check file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('File type not supported. Use PDF, DOC, DOCX, TXT, JPG, or PNG');
        return;
      }
      setSelectedFile(file);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full">Pending</span>;
      case 'submitted':
        return <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">Submitted</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">Completed</span>;
      case 'overdue':
        return <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">Overdue</span>;
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysUntil = (dateString) => {
    if (!dateString) return '';
    const due = new Date(dateString);
    const now = new Date();
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `${diffDays} days left`;
  };

  // Get due date from either due_date or deadline field
  const getDueDate = (assignment) => {
    return assignment.due_date || assignment.deadline;
  };

  // Download attached file
  const handleDownloadFile = async (assignment) => {
    if (!assignment.attached_file) return;
    
    try {
      const token = localStorage.getItem('access_token');
      const fileId = assignment.attached_file.id;
      const response = await fetch(`${API_URL}/api/assignments/file/${fileId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = assignment.attached_file.file_name || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  if (assignments.length === 0) {
    return null; // Don't show widget if no assignments
  }

  const pendingAssignments = assignments.filter(a => a.status === 'pending' || a.status === 'overdue');
  const completedAssignments = assignments.filter(a => a.status === 'completed');

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <BookOpen className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Assignments & Deadlines</h2>
            <p className="text-sm text-gray-600">
              {pendingAssignments.length} pending · {completedAssignments.length} completed
            </p>
          </div>
        </div>
      </div>

      {/* Pending/Overdue Assignments */}
      {pendingAssignments.length > 0 && (
        <div className="space-y-4 mb-6">
          {pendingAssignments.map((assignment) => (
            <div 
              key={assignment.id} 
              className={`border-l-4 p-4 rounded-lg ${
                assignment.status === 'overdue' 
                  ? 'border-red-500 bg-red-50' 
                  : 'border-orange-500 bg-orange-50'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="font-semibold text-gray-900">{assignment.title}</h3>
                    {getStatusBadge(assignment.status)}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{assignment.description}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(getDueDate(assignment))}</span>
                    </span>
                    <span className={`flex items-center space-x-1 ${
                      assignment.status === 'overdue' ? 'text-red-600 font-semibold' : ''
                    }`}>
                      <Clock className="w-4 h-4" />
                      <span>{getDaysUntil(getDueDate(assignment))}</span>
                    </span>
                    {assignment.is_global && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                        All Mentees
                      </span>
                    )}
                  </div>
                  
                  {/* Attached File Download */}
                  {assignment.attached_file && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="text-sm font-medium text-blue-900">Assignment Materials</p>
                            <p className="text-xs text-blue-600">{assignment.attached_file.file_name}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownloadFile(assignment)}
                          className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {assignment.status === 'pending' && submitting !== assignment.id && (
                <button
                  onClick={() => setSubmitting(assignment.id)}
                  className="mt-3 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium flex items-center space-x-2"
                >
                  <Send className="w-4 h-4" />
                  <span>Submit Assignment</span>
                </button>
              )}

              {submitting === assignment.id && (
                <div className="mt-3 space-y-3">
                  <textarea
                    value={submitNotes}
                    onChange={(e) => setSubmitNotes(e.target.value)}
                    placeholder="Add notes about your submission..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    rows={3}
                  />
                  
                  {/* File Upload Section */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Attach File (Optional)
                    </label>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                      className="hidden"
                    />
                    {!selectedFile ? (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-colors flex items-center justify-center gap-2 text-gray-600"
                      >
                        <Upload className="w-5 h-5" />
                        <span>Choose File</span>
                      </button>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-green-600" />
                          <span className="text-sm text-green-700 truncate max-w-[200px]">
                            {selectedFile.name}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedFile(null)}
                          className="p-1 hover:bg-green-100 rounded"
                        >
                          <X className="w-4 h-4 text-green-600" />
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      Max 10MB. Supported: PDF, DOC, DOCX, TXT, JPG, PNG
                    </p>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => submitAssignment(assignment.id)}
                      disabled={uploading}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium disabled:bg-orange-300 flex items-center gap-2"
                    >
                      {uploading ? (
                        <>
                          <span className="animate-spin">⏳</span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Submit
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setSubmitting(null);
                        setSubmitNotes('');
                        setSelectedFile(null);
                      }}
                      disabled={uploading}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium disabled:bg-gray-100"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Recently Completed */}
      {completedAssignments.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Recently Completed</h3>
          <div className="space-y-2">
            {completedAssignments.slice(0, 3).map((assignment) => (
              <div 
                key={assignment.id} 
                className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200"
              >
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{assignment.title}</p>
                    <p className="text-xs text-gray-600">
                      Completed on {formatDate(assignment.completed_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {pendingAssignments.length === 0 && completedAssignments.length > 0 && (
        <div className="text-center py-4 text-green-600">
          <CheckCircle className="w-12 h-12 mx-auto mb-2" />
          <p className="font-semibold">All caught up!</p>
          <p className="text-sm text-gray-600">No pending assignments</p>
        </div>
      )}
    </div>
  );
};

export default AssignmentsWidget;
